import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import { LoadingPage } from './Spinner';
import {
  LayoutDashboard,
  BarChart3,
  Clock,
  Wallet,
  History,
  ListOrdered,
  Settings,
  Users,
  LogOut,
  Menu,
  X,
} from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

const navItems = [
  { href: '/', label: 'لوحة التحكم', icon: LayoutDashboard },
  { href: '/analytics', label: 'التحليلات', icon: BarChart3 },
  { href: '/time-analytics', label: 'تحليل الأوقات', icon: Clock },
  { href: '/queue', label: 'الطابور', icon: ListOrdered },
  { href: '/settlements', label: 'التسويات', icon: Wallet },
  { href: '/history', label: 'السجل', icon: History },
  { href: '/settings', label: 'الإعدادات', icon: Settings },
  { href: '/staff', label: 'الموظفون', icon: Users },
];

export default function Layout({ children }: LayoutProps) {
  const { user, isLoading, isAuthenticated, logout } = useAuth();
  const [location] = useLocation();
  const [, navigate] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (isLoading) return <LoadingPage />;

  if (!isAuthenticated) {
    navigate('/login');
    return <LoadingPage />;
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 right-0 z-50 w-64 transform bg-white shadow-card transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b border-gray-100 px-5">
          <Link href="/" className="flex items-center gap-2.5">
            <img src="/ramba-logo.png" alt="Ramba" className="w-9 h-9 rounded-xl" />
            <div>
              <span className="text-base font-bold text-gray-900 block leading-tight">Ramba Business</span>
              <span className="text-[10px] text-gray-400">رمبا للأعمال</span>
            </div>
          </Link>
          <button
            className="rounded-xl p-2 hover:bg-gray-100 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Shop name */}
        <div className="px-5 py-3 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-900 truncate">{user?.shopNameAr || user?.shopName}</p>
          <p className="text-xs text-gray-400 truncate">{user?.name}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-3">
          {navItems.map((item) => {
            const isActive = location === item.href ||
              (item.href !== '/' && location.startsWith(item.href));
            const Icon = item.icon;

            return (
              <Link
                key={item.href}
                href={item.href}
                className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white shadow-card'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className={`h-4.5 w-4.5 ${isActive ? 'text-white' : ''}`} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="border-t border-gray-100 p-3">
          <button
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="h-4.5 w-4.5" />
            <span>تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:mr-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-4 bg-white/80 backdrop-blur-sm px-4 lg:px-6 border-b border-gray-100">
          <button
            className="rounded-xl p-2 hover:bg-gray-100 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
